"use client";

import Image from "next/image";
import Link from "next/link";
import {
  EfficiencyLabelShort,
  type TEfficiencyLabel,
  isValidEfficiencyLabel,
} from "./efficiency-label";
import ImageFallback from "@/app/components/ui/image-fallback";
import type { PriceTextResultT } from "@/app/utils/helpers/backend/offers/offer-card/offer-card-helpers";
import type { ReactNode } from "react";
import { useBrand } from "@/app/components/theme/brand-theme-boundary";
import { cn } from "@/lib/utils";

export default function OfferCard({
  image,
  title,
  efficiency,
  price,
  offer_types,
  efficiencyLabel,
  hybridLabels,
  href,
  listPriceSavingsBadge,
}: {
  image?: string;
  title: string;
  efficiency: string;
  offer_types: string[];
  price: string | PriceTextResultT;
  efficiencyLabel?: TEfficiencyLabel;
  hybridLabels?: {
    co2LabelGewichtetKombiniert?: TEfficiencyLabel;
    co2LabelBatterieEntladen?: TEfficiencyLabel;
  };
  href?: string;
  listPriceSavingsBadge?: ReactNode;
}) {
  const { isSkoda, isAudi } = useBrand();
  const hasConsumptionData =
    (efficiency?.trim()?.length ?? 0) > 0 ||
    efficiencyLabel != null ||
    hybridLabels?.co2LabelGewichtetKombiniert != null ||
    hybridLabels?.co2LabelBatterieEntladen != null;

  const skodaCardClass =
    "rounded-none border-0 bg-white text-black shadow-[0_6px_16px_hsl(0_0%_20%_/_0.10)] hover:bg-white";
  const audiCardClass =
    "rounded-[20px] border-0 bg-[#1D2129] text-white shadow-none hover:bg-[#252b36]";

  // Plain JSX value (not an inline component) so React does not remount the card subtree on every render
  const cardContent = (
    <div
      className={cn(
        "bg-background flex h-full w-full min-w-[260px] flex-col overflow-hidden rounded-lg border text-black transition-colors hover:bg-[#e9e9e9]",
        isSkoda && skodaCardClass,
        isAudi && audiCardClass
      )}
    >
      {image ? (
        <Image
          src={image}
          alt={title || "Angebot Bild"}
          width={600}
          height={338}
          className={cn(
            "aspect-[16/9] w-full shrink-0 rounded-t-lg object-cover",
            isSkoda && "rounded-none",
            isAudi && "rounded-t-[20px]"
          )}
        />
      ) : (
        <ImageFallback
          className={cn(
            "aspect-[16/9] shrink-0 rounded-t-lg",
            isSkoda && "rounded-none",
            isAudi && "rounded-t-[20px]"
          )}
        />
      )}
      <div className="flex flex-1 flex-col p-4">
        <div className="flex shrink-0 flex-row items-center justify-start gap-2">
          <h3
            className={cn("heading-small text-black", isAudi && "text-white")}
          >
            {title}
          </h3>
          {offer_types?.map((type, index) => (
            <div
              key={`${type}-${index}`}
              className={cn(
                "bg-light-blue shrink-0 rounded-sm px-1 py-0.5 text-xs",
                isAudi && "bg-[#2d3340]"
              )}
            >
              <span
                className={cn("font-normal text-black", isAudi && "text-white")}
              >
                {type}
              </span>
            </div>
          ))}
        </div>
        {hasConsumptionData && (
          <p className="text-muted-foreground min-h-[3rem] flex-1 p-0 pt-1 text-xs whitespace-pre-line">
            {hybridLabels?.co2LabelGewichtetKombiniert ||
            hybridLabels?.co2LabelBatterieEntladen ? (
              efficiency.split("\n").map((line, index) => {
                const label =
                  index === 0
                    ? hybridLabels.co2LabelGewichtetKombiniert
                    : index === 1
                      ? hybridLabels.co2LabelBatterieEntladen
                      : undefined;

                return (
                  <span key={index}>
                    {line}
                    {label && isValidEfficiencyLabel(label) && (
                      <EfficiencyLabelShort
                        className="mb-0.5 ml-1 inline"
                        label={label}
                      />
                    )}
                    {index < efficiency.split("\n").length - 1 && <br />}
                  </span>
                );
              })
            ) : (
              <>
                <span>{efficiency}</span>
                {efficiencyLabel && (
                  <EfficiencyLabelShort
                    className="mb-0.5 ml-1 inline"
                    label={efficiencyLabel}
                  />
                )}
              </>
            )}
          </p>
        )}
        <div
          className={cn(
            "mt-auto shrink-0 pt-2 text-black",
            isAudi && "text-white"
          )}
        >
          {listPriceSavingsBadge && (
            <div className="pb-2">{listPriceSavingsBadge}</div>
          )}
          <div className="font-semibold">
            {typeof price === "string" ? (
              price
            ) : (
              <>
                <span>
                  {price.pricingLabel} {price.price}
                </span>
                {price.vatLabel && (
                  <span className="text-secondary-foreground ml-2 text-xs font-normal">
                    {price.vatLabel}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (href) {
    return (
      <Link
        href={href}
        prefetch={false}
        className={cn(
          "block h-full",
          isSkoda && "text-black hover:text-black",
          isAudi && "text-white hover:text-white"
        )}
      >
        {cardContent}
      </Link>
    );
  }

  return cardContent;
}
